import { Component, EventEmitter, OnInit, Output, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { validateIBAN } from '@commons';
import { GenericResponse, Select } from '@interfaces';
import { UtilService } from 'src/app/services/util.service';
import { HiringData } from '../../new-contract-large-accounts.model';
import { NewContractLargeAccountsService } from '../../new-contract-large-accounts.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UtilValidatorsService } from 'src/app/commons/validations-format/util-validators.service';

@Component({
  selector: 'app-new-contract-hiring-data',
  templateUrl: './new-contract-hiring-data.component.html',
  styleUrls: ['./new-contract-hiring-data.component.scss']
})
export class NewContractHiringDataComponent implements OnInit, OnDestroy {
  public hiringDataForm: FormGroup = new FormGroup({
    startContract: new FormControl(null, Validators.required),
    endContract: new FormControl(null, Validators.required),
    viaType: new FormControl(null, Validators.required),
    roadName: new FormControl(null, [Validators.required, Validators.maxLength(50)]),
    number: new FormControl(null, [Validators.maxLength(8), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]),
    gate: new FormControl(null, Validators.maxLength(10)),
    stair: new FormControl(null, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]),
    floor: new FormControl(null, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]),
    door: new FormControl(null, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]),
    postalCode: new FormControl(null, [Validators.required, Validators.minLength(5)]),
    town: new FormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(50)]),
    province: new FormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(50)]),
    iban: new FormControl(null, Validators.required),
    entity: new FormControl(null, Validators.required),
    office: new FormControl(null, Validators.required),
    dc: new FormControl(null, Validators.required),
    account: new FormControl(null, Validators.required),
    name: new FormControl(null, [Validators.required, Validators.maxLength(50)]),
    surname: new FormControl(null, [Validators.required, Validators.maxLength(50)]),
    dni: new FormControl(null, [Validators.required, this.utilValidatorsService.validateDNI]),
    email: new FormControl(
      null,
      [Validators.required, Validators.pattern(this.utilValidatorsService.emailPattern), Validators.maxLength(50)]
    ),
    phone: new FormControl(null, [Validators.required, this.utilValidatorsService.validateMobile, Validators.maxLength(9)])
  });
  public viaTypesList: Select[] = [];
  public secondSigner = false;
  public townList: Select[] = [];
  public today: Date = new Date();
  private destroyed$: Subject<boolean> = new Subject<boolean>();

  @Output() canGoNext: EventEmitter<HiringData> = new EventEmitter();
  @Output() changeLoader: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private newContractLargeAccountsService: NewContractLargeAccountsService,
    private utilService: UtilService,
    private utilValidatorsService: UtilValidatorsService,
  ) { }

  ngOnInit(): void {
    this.hiringDataForm.setValidators(this.validateIBANControls);
    this.getViasTypes();
    this.subscribeFormControls();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private getViasTypes(): void {
    this.changeLoader.emit(true);
    this.newContractLargeAccountsService.getViasTypes().pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse) => {
        if (response.success) {
          this.viaTypesList = response.data.map(value => ({ label: value.nombreTipoVia, value: value.idTipoVia }));
        } else {
          this.utilService.toastErrors(response);
        }
        this.changeLoader.emit(false);
      },
      error => {
        this.changeLoader.emit(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private subscribeFormControls(): void {
    this.hiringDataForm.get('postalCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.hiringDataForm.get('town').setValue(null);
      this.hiringDataForm.get('province').setValue(null);
      this.townList = [];
      if (value && value.length > 4) {
        this.changeLoader.emit(true);
        this.newContractLargeAccountsService.getPostalCode(value).pipe(takeUntil(this.destroyed$)).subscribe(
          (response: GenericResponse) => {
            if (response.success) {
              this.townList = response.data.poblaciones?.map(town => ({
                label: town.nombrePoblacion,
                value: town.codigoPoblacion
              })) || [];
              if (this.townList?.length > 1) {
                this.hiringDataForm.get('town').enable();
              } else {
                this.hiringDataForm.get('town').setValue(this.townList[0]?.label);
                this.hiringDataForm.get('town').disable();
              }
              this.hiringDataForm.get('province').setValue(response.data.nombreProvincia);
            } else {
              this.utilService.toastErrors(response);
            }
            this.changeLoader.emit(false);
          },
          error => {
            this.changeLoader.emit(false);
            this.utilService.toastErrors(error);
          }
        );
      }
    });
    this.hiringDataForm.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      const values: HiringData = {
        ...{
          valid: value === 'VALID' && this.hiringDataForm.get('town').value && this.hiringDataForm.get('province').value,
          townLabel: this.townList?.find(res => this.hiringDataForm.get('town')?.value === res?.value)?.label
        },
        ...this.hiringDataForm.getRawValue()
      };
      values.viaType = this.viaTypesList?.find(road => (road?.value === this.hiringDataForm.get('viaType')?.value))?.label;
      if (this.townList?.length === 1) {
        values.town = this.townList[0]?.value;
        values.townLabel = this.townList[0]?.label;
      }
      this.canGoNext.emit(values);
    });
  }

  private validateIBANControls(control: AbstractControl): ValidationErrors | null {
    const iban = control.get('iban').value;
    const entity = control.get('entity').value;
    const office = control.get('office').value;
    const dc = control.get('dc').value;
    const account = control.get('account').value;
    if (!validateIBAN(iban + entity + office + dc + account)) {
      return { invalid_IBAN: true };
    }
    return null;
  }

  public handleAddSigner(): void {
    this.hiringDataForm.addControl('name2', new FormControl(null, [Validators.required, Validators.maxLength(50)]));
    this.hiringDataForm.addControl('surname2', new FormControl(null, [Validators.required, Validators.maxLength(50)]));
    this.hiringDataForm.addControl('dni2', new FormControl(null, [Validators.required, this.utilValidatorsService.validateDNI]));
    this.hiringDataForm.addControl('email2', new FormControl(null, [
      Validators.required, Validators.pattern(this.utilValidatorsService.emailPattern), Validators.maxLength(50)
    ]));
    this.hiringDataForm.addControl('phone2', new FormControl(
      null,
      [Validators.required, this.utilValidatorsService.validateMobile, Validators.maxLength(9)]
    ));
    this.secondSigner = true;
  }

  public handleDeleteSigner(): void {
    this.hiringDataForm.removeControl('name2');
    this.hiringDataForm.removeControl('surname2');
    this.hiringDataForm.removeControl('dni2');
    this.hiringDataForm.removeControl('email2');
    this.hiringDataForm.removeControl('phone2');
    this.secondSigner = false;
  }
}
